import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import 'proj4leaflet';
import { Location, LocationSystem, roundToNumber } from '../types';

const pt: Record<string, string> = {
	'EPSG:25833': '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	'EPSG:25832': '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	'EPSG:4326': '+proj=longlat +datum=WGS84 +no_defs +type=crs',
	'EPSG:4258': '+proj=longlat +ellps=GRS80 +no_defs +type=crs',
	'EPSG:3034': '+proj=lcc +lat_0=52 +lon_0=10 +lat_1=35 +lat_2=65 +x_0=4000000 +y_0=2800000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	'EPSG:3035': '+proj=laea +lat_0=52 +lon_0=10 +x_0=4321000 +y_0=3210000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	'EPSG:3044': '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	'EPSG:3045': '+proj=utm +zone=33 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	'EPSG:3857': '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs',
	'EPSG:4839': '+proj=lcc +lat_0=51 +lon_0=10.5 +lat_1=48.6666666666667 +lat_2=53.6666666666667 +x_0=0 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	'EPSG:5650': '+proj=tmerc +lat_0=0 +lon_0=15 +k=0.9996 +x_0=33500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	'EPSG:4647': '+proj=tmerc +lat_0=0 +lon_0=9 +k=0.9996 +x_0=32500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs +type=crs',
	// Gauß-Krüger Zonen (Bessel-Ellipsoid mit Datum-Shift nach WGS84)
	'EPSG:31466': '+proj=tmerc +lat_0=0 +lon_0=6 +k=1 +x_0=2500000 +y_0=0 +ellps=bessel +towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs +type=crs',
	'EPSG:31467': '+proj=tmerc +lat_0=0 +lon_0=9 +k=1 +x_0=3500000 +y_0=0 +ellps=bessel +towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs +type=crs',
	'EPSG:31468': '+proj=tmerc +lat_0=0 +lon_0=12 +k=1 +x_0=4500000 +y_0=0 +ellps=bessel +towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs +type=crs',
	'EPSG:31469': '+proj=tmerc +lat_0=0 +lon_0=15 +k=1 +x_0=5500000 +y_0=0 +ellps=bessel +towgs84=598.1,73.7,418.2,0.202,0.045,-2.455,6.7 +units=m +no_defs +type=crs'
};

@Injectable({
	providedIn: 'root'
})
export class TransformationService {
	from(c: Location) {
		if (Object.keys(pt).indexOf(c.system) >= 0)
			return this._from(c.system, c.x, c.y);
		else {
			alert(`Unbekanntes Koordinatensystem ${c.system}`);
			return this._from(LocationSystem.EPSG5650, c.x, c.y);
		}
	}

	private _from(cs: string, x: number, y: number) {
		const crs = new L.Proj.CRS(cs, pt[cs]);
		const unproj = crs.unproject(L.point(x, y));
		return unproj;
	}

	maybeEPSG25833(x: number, y: number) {
		try {
			const crs = new L.Proj.CRS('EPSG:25833', pt['EPSG:25833']);
			const unproj = crs.unproject(L.point(x, y));
			return Number.isFinite(unproj.lat) && !Number.isNaN(unproj.lat) && Number.isFinite(unproj.lng) && !Number.isNaN(unproj.lng);
		} catch (error) {
			return false;
		}
	}

	toEPSG5650(latLng: L.LatLng) {
		const crs = new L.Proj.CRS('EPSG:5650', pt['EPSG:5650']);
		const proj = crs.project(latLng);
		proj.x = roundToNumber(proj.x, 0)!;
		proj.y = roundToNumber(proj.y, 0)!;
		return proj;
	}

	/**
	 * Transformiert WGS84 LatLng in ein beliebiges unterstütztes Koordinatensystem.
	 */
	toLocation(latLng: L.LatLng, system: LocationSystem): Location {
		const systemStr = system as string;
		if (!pt[systemStr]) {
			console.error(`[TransformationService] Unbekanntes Koordinatensystem: ${systemStr}`);
			return { system, x: 0, y: 0 };
		}
		// Geographische CRS (Grad) brauchen volle Dezimalpräzision,
		// projizierte CRS (Meter) können auf Ganzzahlen gerundet werden.
		const isGeographic = pt[systemStr].includes('+proj=longlat');
		const digits = isGeographic ? 12 : 0;
		const crs = new L.Proj.CRS(systemStr, pt[systemStr]);
		const proj = crs.project(latLng);
		return {
			system,
			x: roundToNumber(proj.x, digits)!,
			y: roundToNumber(proj.y, digits)!
		};
	}

	maybeEPSG5650(x: number, y: number) {
		try {
			const crs = new L.Proj.CRS('EPSG:5650', pt['EPSG:5650']);
			const unproj = crs.unproject(L.point(x, y));
			return Number.isFinite(unproj.lat) && !Number.isNaN(unproj.lat) && Number.isFinite(unproj.lng) && !Number.isNaN(unproj.lng);
		} catch (error) {
			return false;
		}
	}
}
